// The words of the "Suitability Fact Find - Existing Clients" v6 form
// (SEP-288). A suitability refresh asks the questions of this form.
//
// The web form and the server both read these constants, so the two always
// show the same words.
//
// # The risk questions
//
// The v6 form asks the twelve scored risk questions in new words. The score
// does not change: each question keeps its `atr_q*` id, and each answer keeps
// the CANONICAL option label that `atr-scoring.ts` scores. The wording below
// changes only what a person reads. The stored answer, the band, the history
// and the suggestion boxes all keep the canonical label, so an answer of an
// onboarding fact find or of a V5 import still matches a v6 option.
//
// Three questions change their option bands as well as their words: q6, q7
// and q12. Each v6 option takes the canonical option at the same position,
// and Compliance must confirm that mapping (SEP-288).

/** The v6 words of one risk question. */
export interface AtrQuestionWording {
    readonly prompt: string;
    /** Canonical option label -> the v6 words. An absent label keeps its words. */
    readonly options?: Readonly<Record<string, string>>;
}

/**
 * The v6 words of the risk questions, by question id. The ids are values, so
 * the map is built from pairs and not written as an object literal.
 */
const ATR_WORDING_PAIRS: readonly (readonly [string, AtrQuestionWording])[] = [
    [
        'atr_q1',
        {
            prompt: 'Protecting my money is more important to me than the opportunity to achieve higher returns.',
        },
    ],
    [
        'atr_q2',
        {
            prompt: 'I would prefer fewer ups and downs in the value of my portfolio, even if this meant accepting lower potential returns.',
        },
    ],
    [
        'atr_q3',
        {
            prompt:
                'Smaller companies can grow faster than larger, well-established companies. However, their shares can be harder to sell, ' +
                'and they are at greater risk of failure. I would be comfortable investing in smaller companies for the potential to achieve a higher return.',
        },
    ],
    [
        'atr_q4',
        {
            prompt:
                'Shares in smaller companies can rise and fall in value more sharply than shares in larger companies. ' +
                'I would be comfortable remaining invested through these fluctuations.',
        },
    ],
    [
        'atr_q5',
        {
            prompt:
                'Investment markets can sometimes rise and fall sharply. If one of your investments fell by 25% over four weeks, ' +
                'what would you be most likely to do?',
        },
    ],
    [
        'atr_q6',
        {
            prompt:
                'If your portfolio fell in value and remained below the amount originally invested for several years, ' +
                'how comfortable would you be remaining invested?',
            options: {
                'Any fall would be cause for concern': 'Very Uncomfortable',
                'Loss of 15%': 'Uncomfortable',
                'Loss of 16-30%': 'Neither Comfortable nor Uncomfortable',
                'Loss of 31-49%': 'Comfortable',
                'Loss of 50%+': 'Very Comfortable',
            },
        },
    ],
    [
        'atr_q7',
        {
            prompt: 'When do you expect to need to withdraw most or all of the money from your portfolio?',
            options: {
                '0-1 years': 'Within 1 year',
                '1-3 years': 'In 1-3 years',
                '3-5 years': 'In 4-5 years',
                '5-7 years': 'In 6-7 years',
                '7+ years': 'More than 8 years',
            },
        },
    ],
    [
        'atr_q8',
        {
            prompt: 'How secure do you consider your current sources of income to be?',
            options: { 'Very unsecure': 'Very insecure', Unsecure: 'Insecure' },
        },
    ],
    [
        'atr_q9',
        {
            prompt: 'Over the next ten years, how do you expect your income to change, if at all?',
            options: { 'Remain stable': 'Remain broadly the same' },
        },
    ],
    [
        'atr_q10',
        {
            prompt: 'Over the next ten years, how do you expect your debts and other financial commitments to change, if at all?',
            options: { 'Remain stable': 'Remain broadly the same' },
        },
    ],
    [
        'atr_q11',
        {
            prompt: 'Approximately how many months of essential expenditure could your readily accessible funds cover?',
        },
    ],
    [
        'atr_q12',
        {
            prompt:
                'How much of your investments with Dowgate do you consider you could lose without significantly affecting ' +
                'your future standard of living?',
            options: {
                'No losses (0-10%)': 'Minimal losses (0-10%)',
                'Moderate losses (21-49%)': 'Moderate losses (21-40%)',
                'Large losses (50-90%)': 'Large losses (41-69%)',
                'Total losses (91-100%)': 'Maximum losses (70-100%)',
            },
        },
    ],
];

export const EXISTING_CLIENT_ATR_WORDING: Readonly<Record<string, AtrQuestionWording>> =
    Object.fromEntries(ATR_WORDING_PAIRS);

/** The words that a reader sees for one stored risk answer. */
export function atrOptionWording(
    wording: Readonly<Record<string, AtrQuestionWording>> | undefined,
    questionId: string,
    canonicalLabel: string,
): string {
    return wording?.[questionId]?.options?.[canonicalLabel] ?? canonicalLabel;
}

/** The investment objectives of the v6 form, in the order the form prints them. */
export const EXISTING_CLIENT_INVESTMENT_OBJECTIVES = [
    'Income',
    'Growth',
    'Balance of Income and Growth',
    'Capital Preservation',
] as const;

/** The note under each objective, word for word from the v6 form. */
export const EXISTING_CLIENT_INVESTMENT_OBJECTIVE_EXPLANATIONS: Readonly<
    Record<(typeof EXISTING_CLIENT_INVESTMENT_OBJECTIVES)[number], string>
> = {
    Income:
        'Your priority is regular payments from your portfolio, for example to supplement your income. ' +
        'Income-focused portfolios often hold more dividend-paying shares and bonds, but their value will still rise and fall.',
    Growth:
        "Your priority is increasing your portfolio's value over time rather than receiving a regular income. " +
        'Growth portfolios may experience more short-term movement in pursuit of long-term gains.',
    'Balance of Income and Growth':
        'You want a mix of regular income and long-term growth. This provides a middle path between income and growth.',
    'Capital Preservation':
        "Your priority is protecting your portfolio's value and limiting losses rather than seeking higher returns. " +
        'Its value may still rise and fall, and no investment can guarantee against loss.',
};

/** The statement under the trusted contact, word for word from the v6 form. */
export const TRUSTED_CONTACT_STATEMENT =
    'By providing these details, you authorise us to contact this person for these purposes and share only the ' +
    'information necessary to explain our concern. The contact role is separate from that of a Nominated ' +
    'Representative or Third-Party Disclosure Contact. Nominating a trusted contact does not authorise them to ' +
    'access your account, receive portfolio information or give instructions on your behalf. You may change or ' +
    'remove your trusted contact at any time.';

// ---------------------------------------------------------------------------
// The client words of the v6 form that other forms quote (SEP-293)
// ---------------------------------------------------------------------------
//
// The web form of a refresh, the onboarding fact find and the client
// application (form version 3.0) ask some questions of the v6 form. Each one
// reads its words here, so the three forms always show the same words.

/** The four answers of a health question of the v6 form. */
export const EXISTING_CLIENT_HEALTH_OPTIONS = ['Excellent', 'Good', 'Fair', 'Poor'] as const;

/** How decisions about investments have generally been made, in the order of the v6 form. */
export const EXISTING_CLIENT_INVESTMENT_DECISIONS = [
    'By me',
    'With financial advice',
    'By a discretionary investment manager',
    'A combination of all',
] as const;

/** How the client uses the account, in the order of the v6 form. */
export const EXISTING_CLIENT_ACCOUNT_USAGES = [
    'To hold and invest a lump sum',
    'To hold investments transferred from elsewhere or make further transfers',
    'To invest regular savings',
    'To receive investment income',
    'To hold cash pending investment',
    'To invest the proceeds of a property sale, inheritance, pension payment or business sale',
    'To invest in a particular company, fund, offer or corporate action',
    'To hold investments within an ISA, SIPP, SSAS or other investment wrapper',
    'Other',
] as const;

/** One asset class of the v6 grid: a key for the ids, the name and its scope. */
export interface ExistingClientAssetClass {
    readonly key: string;
    readonly name: string;
    readonly scope: string;
}

/** The six asset classes of the v6 grid, in the order of the form. */
export const EXISTING_CLIENT_ASSET_CLASSES: readonly ExistingClientAssetClass[] = [
    {
        key: 'cash_deposits',
        name: 'Cash and deposits',
        scope: 'savings accounts and fixed-term deposits',
    },
    { key: 'fixed_income', name: 'Fixed income', scope: 'including gilts and corporate bonds' },
    { key: 'equities', name: 'Equities', scope: 'including listed and smaller-company shares' },
    { key: 'funds', name: 'Funds', scope: 'including OEICs, investment trusts and ETFs' },
    {
        key: 'alternatives',
        name: 'Alternatives',
        scope: 'including property, commodities, private equity and hedge funds',
    },
    {
        key: 'derivatives',
        name: 'Derivatives and structured products',
        scope: 'including options, futures and CFDs',
    },
];

/** The v6 question "have you invested in this yourself?" of one asset class. */
export function existingClientAssetInvestedLabel(assetClass: ExistingClientAssetClass): string {
    return `${assetClass.name} — have you invested in this yourself?`;
}

/** The v6 question "could you explain how it works?" of one asset class. */
export function existingClientAssetExplainLabel(assetClass: ExistingClientAssetClass): string {
    return `${assetClass.name} — could you explain how it works and its main risks to a friend?`;
}

/** The note under both questions of one asset class. */
export function existingClientAssetScope(assetClass: ExistingClientAssetClass): string {
    return `${assetClass.name}: ${assetClass.scope}.`;
}

/** The "Personal % ownership" box of one money row of the v6 form. */
export function existingClientOwnershipLabel(rowLabel: string): string {
    return `${rowLabel} — personal % ownership`;
}

/** The note under each ownership box of the v6 form. */
export const EXISTING_CLIENT_OWNERSHIP_WHY =
    'Your personal share tells us how much of the figure is yours.';

/**
 * The labels of the money rows of the v6 form that the client application
 * also asks. The keys are the ids of the fact find, in camelCase.
 */
export const EXISTING_CLIENT_MONEY_ROW_LABELS = {
    mainPropertyValue: 'Value of your main property',
    otherPropertyValue: 'Value of other property',
    otherInvestmentsValue:
        'Value of other investments held externally, including listed investments',
    cashSavingsNsi: 'Value of your cash, savings, Premium Bonds and other NS&I products',
    businessInterestsValue: 'Value of business interests, EIS and unquoted investments',
    otherMaterialAssetsValue: 'Value of other material assets such as chattels, antiques',
    mortgageOutstanding: 'Mortgage outstanding on your main residence',
    mortgagePayoffDate: 'Anticipated mortgage pay-off date',
    otherMaterialDebtsAmount: 'All other material debts',
    otherMaterialDebtsDetail: 'Please provide details of all other material debts',
    statePensionIncome: 'State & DB pension & annuity income, including final salary',
    otherPensionIncome: 'Other personal pension income',
    otherInvestmentIncome: 'Other investment income',
    allOtherIncome: 'All other income (including sources of income)',
    annualMortgageRent: 'Annual mortgage / rent expenditure',
    annualEssentialExpenditure: 'Annual essential expenditure',
    annualDiscretionaryExpenditure: 'Annual discretionary expenditure',
} as const;

/** The other client words of the v6 form that another form quotes, by meaning. */
export const EXISTING_CLIENT_WORDING = {
    employmentNotWorking: 'Not currently working',
    physicalHealth: 'How would you describe your current physical health?',
    mentalHealth: 'How would you describe your current mental health and emotional wellbeing?',
    healthDetail: 'If you have selected Fair or Poor, please provide further detail',
    communicationNeeds:
        'Do you have any communication or support needs that you would like us to consider when providing our services to you?',
    communicationNeedsDetail:
        'Please provide further detail of your communication or support needs',
    trustedContact:
        'Would you like to nominate someone we may contact if we cannot reach you after reasonable attempts, or if we have concerns about your wellbeing?',
    trustedContactDetails:
        'Trusted contact — full name, relationship to you, telephone number and email address',
    lpaRegistered:
        'Do you have a registered Property & Financial Affairs Lasting Power of Attorney in place?',
    lpaRegisteredWhy:
        'We may request further information or certification to verify the LPA if registered and attorney authority.',
    investmentObjective: 'What best describes your current investment objective?',
    experienceLength: 'How long have you been investing, excluding cash savings?',
    knowledgeableInvestor: 'I understand how investments work and the risks involved.',
    workedInFinance: 'Have you ever worked in a financial services role involving investments?',
    workedInFinanceDetail: 'Please provide details of your role and relevant experience',
    workedInFinanceDetailWhy:
        'For example investment management, financial advice, trading, investment research or another investment-related role.',
    financialQualifications:
        'Do you hold, or have you previously held, any relevant financial or investment qualifications?',
    financialQualificationDetail: 'Please provide details of your qualifications',
    financialQualificationDetailWhy: 'For example CFA, IMC or relevant CISI or CII qualifications.',
    otherProductsExperience:
        'Please provide details of any other investments or financial products you have experience of, including approximately how long you have invested in them',
    investmentDecisions:
        'Which of the following best describes how decisions about your investments have generally been made?',
    accountUsage:
        'How do you currently use, or expect to use, your Dowgate account(s)? Please select all that apply.',
    accountUsageOther:
        'Please briefly explain the main purpose of this account and how you expect to use it',
    regularWithdrawalsPortfolio: 'Regular withdrawals — portfolio',
    regularWithdrawalsPortfolioPlaceholder: 'e.g. ISA, GIA or SIPP',
    withdrawalsEssential:
        'Will any of these withdrawals be used to meet essential living costs or other essential expenditure?',
    withdrawalsEssentialDetail: 'Please provide details of the essential costs',
    capitalWithdrawalPurpose: 'Capital withdrawals — purpose',
    furtherContributions:
        'Do you intend to make any further contributions to your Dowgate portfolio?',
    contributionAmount: 'Further contributions — amount',
    contributionFrequency: 'Further contributions — frequency',
    contributionSourceOfFunds: 'Further contributions — source of funds',
    investmentRestrictions:
        'If you have any ethical, environmental, geographic, sector or stock-specific restrictions, please provide these in as much detail as possible',
} as const;

/**
 * The words of the v6 form for a new client (Dowgate decision of 2026-10-21).
 * Each new fact find of an onboarding asks the v6 form, and these words take
 * the place of the words for an existing client. The firm approved them.
 */
export const EXISTING_CLIENT_ONBOARDING_WORDING = {
    accountUsage: 'How do you expect to use your Dowgate account(s)? Please select all that apply.',
    regularWithdrawals: 'Will you require regular withdrawals from your Dowgate portfolio?',
    furtherContributions:
        'Do you intend to make any further contributions after your initial investment?',
} as const;

/**
 * The words of the estate planning questions of the fact find. The onboarding
 * fact find asks all six, the v6 form asks three of them, and the client
 * application asks all six (SEP-293.2).
 */
export const FACT_FIND_WILL_WORDING = {
    hasWill: 'Do you have a will in place?',
    lawFirm: 'Law firm holding your will',
    firmContactName: 'Law firm — contact name',
    firmContactDetails: 'Law firm — contact details',
    lastReview: 'Date your will was last reviewed',
    executorsContact: 'Executors — contact details',
} as const;
